<template>
  <v-container>
    <v-card :loading="store.loading_data">
      <v-card-title class="text-h5">
        {{ t('issue_list') }}
      </v-card-title>
      <v-card-text>
        <v-row class="mx-10 align-center text-center" style="text-align: center" justify="center">
          <v-col cols="12" md="2">
            <v-autocomplete
              multiple
              :items="priorities"
              item-title="text"
              item-value="value"
              :label="t('filter_prio')"
              v-model="selected_prio"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              :label="t('filter_park')"
              :items="parks_with_issues"
              v-model="selected_parks"
              item-title="name"
              item-value="id"
              multiple
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-autocomplete
              :label="t('filter_creator')"
              :items="creators_with_issues"
              v-model="selected_creators"
              item-title="username"
              item-value="id"
              multiple
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
              :label="t('filter_agency')"
              :items="agencies_with_issues"
              item-title="name"
              item-value="id"
              multiple
              v-model="selected_agencies"
            />
          </v-col>
          <v-col class="d-flex align-center justify-center" cols="12" md="2">
            <v-autocomplete
              :label="t('filter_solved')"
              v-model="selected_solved"
              :items="solved_list"
              item-title="text"
              item-value="value"
              multiple
            />
          </v-col>
        </v-row>
        <v-row class="mx-10 align-center text-center d-none d-md-flex" style="text-align: center" justify="center">
          <v-col cols="6" md="1">{{ 'ID' }}</v-col>
          <v-col cols="6" md="1">{{ t('park_name') }}</v-col>
          <v-col cols="6" md="1">{{ t('opened') }}</v-col>
          <v-col cols="6" md="1">{{ t('creator') }}</v-col>
          <v-col cols="6" md="2">{{ t('title') }}</v-col>
          <v-col cols="6" md="1">{{ t('is_solved') }}</v-col>
          <v-col cols="6" md="1">{{ t('intervention_date') }}</v-col>
          <v-col cols="6" md="1">{{ t('agency') }}</v-col>
          <v-col cols="6" md="2">{{ t('address') }}</v-col>
          <v-col cols="6" md="1">{{ t('actions') }}</v-col>
        </v-row>
        <v-divider/>
        <div style="display: none">
          <ticket-display
            v-if="force_issue_dialog"
            :ticket="issue_to_display"
            :is_open="true"
          />
        </div>
        <v-virtual-scroll
          class="mx-10 my-2"
          v-if="displayed_tickets.length > 0 && !store.loading_data"
          :items="displayed_issues"
          height="500"
          item-height="50"
          :min-item-height="40"
        >
          <template v-slot:default="{ item }">
            <ticket-display
              v-if="item !== null && item !== undefined && item.is_open !== null && item.is_open !== undefined"
              :ticket="item"
              :is_open="false"
            />
            <v-divider/>
          </template>
        </v-virtual-scroll>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn @click="store.load_tickets">
          <v-icon icon="mdi-refresh" class="mr-2"/>
          {{ t('load_tickets') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup>
import { useIssueStore } from "../store/issues_store.js";
import { useI18n } from "vue-i18n";
import TicketDisplay from "./TicketDisplay.vue";
import { computed, onMounted, ref, toRefs, watch } from 'vue';

const props = defineProps({
  ticket: {
    type: Number,
    required: true
  }
});

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

const store = useIssueStore();
const { ticket_list, displayed_tickets, parks_with_issues, agencies_with_issues, creators_with_issues } = toRefs(store);
const priorities = ref([
  { value: 1, text: 'High', color: 'red' },
  { value: 2, text: 'Medium', color: 'orange' },
  { value: 3, text: 'Low', color: 'green' }
]);
const solved_list = ref([
  { value: false, text: 'Unsolved' },
  { value: true, text: 'Solved' }
]);

const force_issue_dialog = ref(false);
const issue_to_display = ref(null);

const selected_solved = ref([false]);
const selected_prio = ref([]);
const selected_parks = ref([]);
const selected_agencies = ref([]);
const selected_creators = ref([]);

const issues_by_prio = computed(() => {
  if (selected_prio.value.length === 0 || selected_prio.value.length === priorities.value.length) {
    return ticket_list.value;
  } else {
    return ticket_list.value.filter(ticket => selected_prio.value.includes(ticket.priority));
  }
});

const issues_by_park = computed(() => {
  if (selected_parks.value.length === 0 || selected_parks.value.length === parks_with_issues.value.length) {
    return  ticket_list.value.map(t => t.id);
  } else {
    return  ticket_list.value.filter(ticket => selected_parks.value.includes(ticket.park.id)).map(t => t.id);
  }
});

const issues_by_agencies = computed(() => {
  if (selected_agencies.value.length === 0 || selected_agencies.value.length === agencies_with_issues.value.length) {
    return ticket_list.value.map(t => t.id);
  } else {
    return ticket_list.value.filter(ticket => selected_agencies.value.includes(ticket.agency.id)).map(t => t.id);
  }
});

const issues_by_creators = computed(() => {
  if (selected_creators.value.length === 0 || selected_creators.value.length === creators_with_issues.value.length) {
    return ticket_list.value.map(t => t.id);
  } else {
    return ticket_list.value.filter(ticket => selected_creators.value.includes(ticket['made_by'])).map(t => t.id);
  }
});

const issues_by_solved = computed(() => {
  if (selected_solved.value.length === 0 || selected_solved.value.length === solved_list.value.length) {
    return ticket_list.value.map(t => t.id);
  } else {
    return ticket_list.value.filter(ticket => selected_solved.value.includes(ticket['is_solved'])).map(t => t.id);
  }
});

const displayed_issues = computed(() => {
  return issues_by_prio.value
    .filter(ticket => issues_by_park.value.includes(ticket.id))
    .filter(ticket => issues_by_agencies.value.includes(ticket.id))
    .filter(ticket => issues_by_creators.value.includes(ticket.id))
    .filter(ticket => issues_by_solved.value.includes(ticket.id));
});


onMounted(async () => {
  await store.load_parks();
});

</script>

<i18n>
{
  "en": {
    "issue_list": "Issue List",
    "new_issue": "New Issue",
    "edit_issue": "Edit Issue",
    "park_name": "Park Name",
    "priority": "Priority",
    "opened": "Issue opened",
    "creator": "Creator",
    "is_solved": "Solved?",
    "title": "Issue title",
    "intervention_date": "Maintenance date",
    "agency": "Agency",
    "address": "Address of the park",
    "actions": "Actions",
    "load_tickets": "Reload Issues",
    "filter_prio": "Filter by priority",
    "filter_park": "Filter by park",
    "filter_creator": "Filter by creator",
    "filter_agency": "Filter by agency",
    "filter_solved": "Filter by status"
  },
  "it": {
    "issue_list": "Lista dei tickets",
    "new_issue": "Nuovo ticket",
    "edit_issue": "Modifica ticket",
    "park_name": "Nome parco",
    "priority": "Priorità",
    "opened": "Ticket aperto il",
    "creator": "Creatore",
    "is_solved": "Risolto?",
    "title": "Titolo del problema",
    "intervention_date": "Data manutenzione",
    "agency": "Agenzia",
    "address": "Indirizzo del parco",
    "actions": "Azioni",
    "load_tickets": "Ricarica i Tickets",
    "filter_prio": "Filtra per priorità",
    "filter_park": "Filtra per parco",
    "filter_creator": "Filtra per creatore",
    "filter_agency": "Filtra per agenzia",
    "filter_solved": "Filtra su stato"
  },
  "fr": {
    "issue_list": "Liste des problèmes",
    "new_issue": "Nouveau problème",
    "edit_issue": "Modifier problème",
    "park_name": "Nom du parc",
    "priority": "Priorité",
    "opened": "Il problème a commencé le",
    "creator": "Créateur",
    "is_solved": "Est-ce résolu?",
    "title": "Titre du problème",
    "intervention_date": "Date de maintenance",
    "agency": "Agence",
    "address": "Adresse du parc",
    "actions": "Actions",
    "load_tickets": "Rafraîchir le problèmes",
    "filter_prio": "Filtrer par priorité",
    "filter_park": "Filtrer par parc",
    "filter_creator": "Filtrer par auteur",
    "filter_agency": "Filtrer par agence",
    "filter_solved": "Filtrer sur l'état"
  },
  "nl": {
    "issue_list": "Probleemlijst",
    "new_issue": "Nieuw probleem",
    "edit_issue": "Bewerk probleem",
    "park_name": "Parkeernaam",
    "priority": "Prioriteit",
    "opened": "Het probleem begon op",
    "creator": "Auteur",
    "is_solved": "Is dit opgelost?",
    "title": "Titel van het probleem",
    "intervention_date": "Datum onderhoud",
    "agency": "Organisatie",
    "address": "Adres van de parkeerplaats",
    "actions": "Acties",
    "load_tickets": "Laad tickets",
    "filter_prio": "Filter op prioriteit",
    "filter_park": "Filter op parc",
    "filter_creator": "Filter op auteur",
    "filter_agency": "Filter op organisatie",
    "filter_solved": "Filteren op opgelost"
  }
}
</i18n>

<style scoped>

</style>
